import { useState } from "react";
import { useDispatch } from "react-redux";

import { Button } from "@mui/material";

import { Tooltip } from "components/Tooltip";

import { i18n } from "services/i18nService";
import {
  addToReorderList,
  generatePos,
} from "services/sosInventoryService/sosApi";
import { handleProgramError } from "services/utility/errors";

import { GenerateTransferDialog } from "views/Reports/jsonReports/GenerateTransferDialog";

import { openAlert } from "globalState/alertSlice";
import {
  loadingIndicatorOn,
  loadingIndicatorOff,
} from "globalState/loadingSlice";

import { ALL_FILTER_OPTION } from "appConstants";

export function ReorderReportActions(props) {
  const { checkedIds, settings } = props;
  const dispatch = useDispatch();
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);

  async function handleAddToReorderList() {
    dispatch(loadingIndicatorOn());

    try {
      const params = {
        itemIds: checkedIds,
        // afterGet.js transforms locations ["0"] → null (V9 treats "All" as null default)
        // Convert back: null → 0 when sending to backend
        locationId: settings.locations === null ? 0 : settings.locations[0].id,
        includePOs: settings.includePOs,
        includeRMAs: settings.includeRMAs,
        includeWOs: settings.includeWOs,
        includeRentals: settings.includeRentals,
        includeNulls: settings.includeNulls,
      };

      const { success, message } = await addToReorderList(params);

      if (success) {
        const successMessage = i18n("alert.SuccessAddedToReorderList");
        dispatch(openAlert({ type: "success", message: successMessage }));
      } else {
        const errorMessage =
          message || i18n("alert.ErrorNotAddedToReorderList");
        handleProgramError(new Error(errorMessage));
      }
    } catch (error) {
      handleProgramError(error);
    } finally {
      dispatch(loadingIndicatorOff());
    }
  }

  async function handleGeneratePos() {
    dispatch(loadingIndicatorOn());

    try {
      const params = {
        items: checkedIds,
        // afterGet.js transforms locations ["0"] → null (V9 treats "All" as null default)
        // Convert back: null → 0 when sending to backend
        locationId: settings.locations === null ? 0 : settings.locations[0].id,
        includePOs: settings.includePOs,
        includeRMAs: settings.includeRMAs,
        includeWOs: settings.includeWOs,
        includeRentals: settings.includeRentals,
        includeNulls: settings.includeNulls,
      };

      const { success, message } = await generatePos(params);

      if (success) {
        const successMessage = i18n("alert.SuccessGeneratePos");
        dispatch(openAlert({ type: "success", message: successMessage }));
      } else {
        const errorMessage = message || i18n("alert.ErrorGeneratingPos");
        handleProgramError(new Error(errorMessage));
      }
    } catch (error) {
      handleProgramError(error);
    } finally {
      dispatch(loadingIndicatorOff());
    }
  }

  const disabled = !checkedIds.length;

  const allLocationIsSelected =
    !settings.locations || ALL_FILTER_OPTION.id === settings.locations[0]?.id;

  function getGenerateTransferTooltip() {
    if (allLocationIsSelected) {
      return i18n("tooltip.CannotTransferFromAllLocations");
    }
    if (disabled) {
      return i18n("tooltip.GenerateTransfer");
    }
    return "";
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title={disabled ? i18n("tooltip.GeneratePurchaseOrders") : ""}>
          <div>
            <Button
              sx={{ lineHeight: "1" }}
              disabled={disabled}
              onClick={handleGeneratePos}
            >
              {i18n("reports.GeneratePOs")}
            </Button>
          </div>
        </Tooltip>
        <Tooltip title={disabled ? i18n("tooltip.AddToReorderList") : ""}>
          <div>
            <Button
              sx={{ lineHeight: "1" }}
              disabled={disabled}
              onClick={handleAddToReorderList}
            >
              {i18n("reports.AddToReorderList")}
            </Button>
          </div>
        </Tooltip>
        <Tooltip title={getGenerateTransferTooltip()}>
          <div>
            <Button
              sx={{ lineHeight: "1" }}
              disabled={disabled || allLocationIsSelected}
              onClick={() => setTransferDialogOpen(true)}
            >
              {i18n("reports.GenerateTransfer")}
            </Button>
          </div>
        </Tooltip>
      </div>

      {transferDialogOpen && (
        <GenerateTransferDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          checkedIds={checkedIds}
          settings={settings}
        />
      )}
    </>
  );
}
